import type { ErrorDetail, ErrorEnvelope, L10n } from '@thesuccesstime/shared';

// Every call to the API (API_PLAN §3, §4.1). The access token lives only in memory; the refresh
// token is an httpOnly cookie on /api/v1/auth, so a reload signs back in without storing anything
// a script could read. The back office always calls /api on its own site (Vite in development,
// Caddy in production).

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ErrorDetail[] = [],
    readonly userMessage?: L10n,
    readonly requestId?: string,
  ) {
    super(message);
  }

  /** Field → message, for forms. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) if (detail.field) out[detail.field] = detail.message ?? detail.code;
    return out;
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let signedOut: (() => void) | null = null;

export const session = {
  set(token: string) {
    accessToken = token;
  },
  clear() {
    accessToken = null;
  },
  /** Called when the session can't be renewed — the app shows the sign-in page. */
  onSignedOut(handler: () => void) {
    signedOut = handler;
  },
  /** Swaps the refresh cookie for a new access token. Calls at the same moment share one request. */
  refresh(): Promise<boolean> {
    refreshing ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then(async res => {
        if (!res.ok) return false;
        const body = (await res.json()) as { data: { accessToken: string } };
        accessToken = body.data.accessToken;
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  },
};

export type Query = Record<string, string | number | boolean | null | undefined>;

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  /** The version last loaded; sent as If-Match so two editors can't overwrite each other. */
  ifMatch?: number;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

export function url(path: string, query?: Query) {
  const target = new URL(BASE + path, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') target.searchParams.set(key, String(value));
  }
  return target.toString();
}

async function toError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ErrorEnvelope;
    const { code, message, details, userMessage, requestId } = body.error;
    return new ApiError(res.status, code, message, details, userMessage, requestId);
  } catch {
    return new ApiError(res.status, 'HTTP_' + res.status, res.statusText || 'The server did not answer as expected');
  }
}

async function send(path: string, options: RequestOptions, retry = true): Promise<Response> {
  const headers: Record<string, string> = { ...options.headers };
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (options.ifMatch !== undefined) headers['if-match'] = `"${options.ifMatch}"`;
  // A CSV import sends the file's text as it is; everything else is JSON.
  const isRaw = options.body instanceof FormData || options.body instanceof Blob;
  if (options.body !== undefined && !isRaw) headers['content-type'] = 'application/json';

  const res = await fetch(url(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : isRaw ? (options.body as BodyInit) : JSON.stringify(options.body),
    credentials: 'include',
    signal: options.signal,
  });

  if (res.status === 401 && retry && !path.startsWith('/auth/')) {
    if (await session.refresh()) return send(path, options, false);
    accessToken = null;
    signedOut?.();
  }
  if (!res.ok) throw await toError(res);
  return res;
}

/** JSON in, JSON out; `undefined` for 204. */
export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await send(path, options);
  if (res.status === 204 || res.headers.get('content-length') === '0') return undefined as T;
  return (await res.json()) as T;
}

/** For a CSV export: fetches with the staff member's token, then hands the file to the browser. */
export async function download(path: string, query: Query | undefined, filename: string, headers?: Record<string, string>) {
  const res = await send(path, { query, headers });
  const blob = await res.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}

/** The message to show a staff member for any error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = error.details.find(d => d.message)?.message;
    return error.userMessage?.en || (error.code === 'VALIDATION_FAILED' && detail ? detail : error.message);
  }
  return error instanceof Error ? error.message : 'Something went wrong';
}
